import type { Noise2D } from '../math/noise';
import type { ParamValues } from '../types';
import { spanToMillimetres } from '../params';
import { TAU, clamp01, lerp, smoothstep } from '../math/scalar';
import { createCellularNoise, createFbm, createSimplexNoise } from '../math/noise';
import {
  PROFILE_OPTIONS,
  type WaveProfile,
  axisDistance,
  pitchAlongAxis,
  waveProfile,
} from './helpers';
import { definePattern } from './registry';

/**
 * Noise-driven patterns — the reliefs that gave the project its name.
 *
 * Every generator is seeded, and the noise is sampled in wall space, so an
 * organic surface stays continuous across panel joints while still reproducing
 * byte-for-byte on the next run.
 */

interface OrganicParams extends ParamValues {
  scale: number;
  octaves: number;
  gain: number;
  lacunarity: number;
  ridged: boolean;
  warp: number;
  warpScale: number;
  seed: number;
}

interface OrganicPrepared {
  field: Noise2D;
  warp: Noise2D;
}

export const organicPattern = definePattern<OrganicParams, OrganicPrepared>({
  id: 'organic',
  name: 'Organic',
  description:
    'Flowing fractal relief. Domain warping bends the noise into itself, which is what turns plain hills into wood grain and marbled stone.',
  category: 'organic',
  tags: ['noise', 'natural', 'seamless'],
  params: [
    {
      kind: 'span',
      key: 'scale',
      label: 'Feature size',
      min: 0.125,
      max: 8,
      step: 0.125,
      default: 1,
      help: 'Size of the largest features, in panels.',
      group: 'Shape',
    },
    {
      kind: 'int',
      key: 'octaves',
      label: 'Octaves',
      min: 1,
      max: 8,
      default: 4,
      help: 'Layers of detail. More octaves need a finer mesh to resolve.',
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'gain',
      label: 'Roughness',
      min: 0.2,
      max: 0.8,
      step: 0.01,
      default: 0.5,
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'lacunarity',
      label: 'Lacunarity',
      min: 1.5,
      max: 3.5,
      step: 0.05,
      default: 2,
      group: 'Detail',
    },
    {
      kind: 'boolean',
      key: 'ridged',
      label: 'Ridged',
      default: false,
      help: 'Folds the noise, producing sharp crests instead of rolling hills.',
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'warp',
      label: 'Warp',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.25,
      help: 'Distorts the field with a second noise.',
      group: 'Shape',
    },
    {
      kind: 'span',
      key: 'warpScale',
      label: 'Warp size',
      min: 0.125,
      max: 8,
      step: 0.125,
      default: 2,
      group: 'Shape',
    },
    { kind: 'int', key: 'seed', label: 'Seed', min: 0, max: 9999, default: 42, group: 'Detail' },
  ],
  prepare(params) {
    const base = createSimplexNoise(params.seed);
    return {
      field: createFbm(base, {
        octaves: params.octaves,
        gain: params.gain,
        lacunarity: params.lacunarity,
        ridged: params.ridged,
      }),
      warp: createSimplexNoise(params.seed ^ 0x5bf03635),
    };
  },
  sample(sample, params, prepared) {
    const { wall } = sample;
    const pitch = (wall.pitchX + wall.pitchY) * 0.5;
    const scale = spanToMillimetres(params.scale, pitch);

    let nx = sample.x / scale;
    let ny = sample.y / scale;

    if (params.warp > 0) {
      const warpScale = spanToMillimetres(params.warpScale, pitch);
      const wx = sample.x / warpScale;
      const wy = sample.y / warpScale;
      nx += prepared.warp(wx, wy) * params.warp;
      ny += prepared.warp(wx + 31.4, wy - 17.2) * params.warp;
    }

    return clamp01(prepared.field(nx, ny) * 0.5 + 0.5);
  },
});

interface CellsParams extends ParamValues {
  cell: number;
  mode: 'cells' | 'edges' | 'crackle' | 'pebbles';
  sharpness: number;
  seed: number;
}

export const cellsPattern = definePattern<
  CellsParams,
  (x: number, y: number) => { f1: number; f2: number }
>({
  id: 'cells',
  name: 'Cells',
  description:
    'Cellular relief built from scattered feature points — pebbles, cracked stone or a honeycomb of raised plates, depending on the mode.',
  category: 'organic',
  tags: ['voronoi', 'natural', 'seamless'],
  params: [
    {
      kind: 'span',
      key: 'cell',
      label: 'Cell size',
      min: 0.0625,
      max: 4,
      step: 0.0625,
      default: 0.35,
      group: 'Shape',
    },
    {
      kind: 'select',
      key: 'mode',
      label: 'Mode',
      options: [
        { value: 'pebbles', label: 'Pebbles — rounded mounds' },
        { value: 'cells', label: 'Cells — raised plates' },
        { value: 'edges', label: 'Edges — raised borders' },
        { value: 'crackle', label: 'Crackle — recessed cracks' },
      ],
      default: 'pebbles',
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'sharpness',
      label: 'Sharpness',
      min: 0.25,
      max: 4,
      step: 0.05,
      default: 1,
      group: 'Detail',
    },
    { kind: 'int', key: 'seed', label: 'Seed', min: 0, max: 9999, default: 5, group: 'Detail' },
  ],
  prepare(params) {
    return createCellularNoise(params.seed);
  },
  sample(sample, params, cellular) {
    const { wall } = sample;
    const pitch = (wall.pitchX + wall.pitchY) * 0.5;
    const cell = spanToMillimetres(params.cell, pitch);

    const { f1, f2 } = cellular(sample.x / cell, sample.y / cell);

    let height: number;
    switch (params.mode) {
      case 'cells':
        height = smoothstep(0, 0.45, f2 - f1);
        break;
      case 'edges':
        height = 1 - smoothstep(0, 0.45, f2 - f1);
        break;
      case 'crackle':
        height = smoothstep(0, 0.2, f2 - f1);
        break;
      case 'pebbles':
      default:
        height = clamp01(1 - f1);
        break;
    }

    return clamp01(Math.pow(height, params.sharpness));
  },
});

interface DunesParams extends ParamValues {
  profile: WaveProfile;
  angle: number;
  wavelength: number;
  turbulence: number;
  turbulenceScale: number;
  drift: number;
  seed: number;
}

export const dunesPattern = definePattern<DunesParams, Noise2D>({
  id: 'dunes',
  name: 'Dunes',
  description:
    'Waves whose crests wander under a noise field. Keeps the readable rhythm of a wave while losing the machine-made regularity.',
  category: 'organic',
  tags: ['wave', 'noise', 'seamless'],
  params: [
    {
      kind: 'select',
      key: 'profile',
      label: 'Profile',
      options: PROFILE_OPTIONS,
      default: 'sine',
      group: 'Shape',
    },
    { kind: 'angle', key: 'angle', label: 'Direction', default: 0, group: 'Shape' },
    {
      kind: 'span',
      key: 'wavelength',
      label: 'Wavelength',
      min: 0.125,
      max: 6,
      step: 0.125,
      default: 1,
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'turbulence',
      label: 'Turbulence',
      min: 0,
      max: 2,
      step: 0.01,
      default: 0.5,
      help: 'How far the crests are allowed to wander, in wavelengths.',
      group: 'Detail',
    },
    {
      kind: 'span',
      key: 'turbulenceScale',
      label: 'Turbulence size',
      min: 0.25,
      max: 10,
      step: 0.25,
      default: 3,
      group: 'Detail',
    },
    {
      kind: 'number',
      key: 'drift',
      label: 'Crest drift',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.3,
      help: 'Modulates the amplitude so crests fade in and out along their length.',
      group: 'Detail',
    },
    { kind: 'int', key: 'seed', label: 'Seed', min: 0, max: 9999, default: 17, group: 'Detail' },
  ],
  prepare(params) {
    const base = createSimplexNoise(params.seed);
    return createFbm(base, { octaves: 3, gain: 0.5, lacunarity: 2, ridged: false });
  },
  sample(sample, params, noise) {
    const { wall } = sample;
    const pitch = pitchAlongAxis(wall.pitchX, wall.pitchY, params.angle);
    const wavelength = spanToMillimetres(params.wavelength, pitch);
    const turbulenceScale = spanToMillimetres(params.turbulenceScale, pitch);

    const nx = sample.x / turbulenceScale;
    const ny = sample.y / turbulenceScale;
    const wander = noise(nx, ny) * params.turbulence * wavelength;

    const distance = axisDistance(sample.x, sample.y, params.angle) + wander;
    let height = waveProfile(distance / wavelength, params.profile);

    if (params.drift > 0) {
      const envelope = 0.5 + 0.5 * noise(nx + 101.7, ny - 53.1);
      height *= lerp(1, envelope, params.drift);
    }

    return clamp01(height);
  },
});

interface FlowParams extends ParamValues {
  scale: number;
  angle: number;
  strength: number;
  bands: number;
  seed: number;
}

export const flowPattern = definePattern<FlowParams, Noise2D>({
  id: 'flow',
  name: 'Flow',
  description:
    'Contour bands following a warped field, giving the layered look of sedimentary rock or a topographic map.',
  category: 'organic',
  tags: ['contour', 'topographic', 'seamless'],
  params: [
    {
      kind: 'span',
      key: 'scale',
      label: 'Feature size',
      min: 0.25,
      max: 10,
      step: 0.25,
      default: 2,
      group: 'Shape',
    },
    { kind: 'angle', key: 'angle', label: 'Stretch direction', default: 0, group: 'Shape' },
    {
      kind: 'number',
      key: 'strength',
      label: 'Stretch',
      min: 1,
      max: 8,
      step: 0.1,
      default: 3,
      help: 'Elongates the field along the direction, producing strata.',
      group: 'Shape',
    },
    {
      kind: 'int',
      key: 'bands',
      label: 'Contour bands',
      min: 2,
      max: 40,
      default: 9,
      group: 'Detail',
    },
    { kind: 'int', key: 'seed', label: 'Seed', min: 0, max: 9999, default: 23, group: 'Detail' },
  ],
  prepare(params) {
    const base = createSimplexNoise(params.seed);
    return createFbm(base, { octaves: 4, gain: 0.5, lacunarity: 2, ridged: false });
  },
  sample(sample, params, noise) {
    const { wall } = sample;
    const pitch = (wall.pitchX + wall.pitchY) * 0.5;
    const scale = spanToMillimetres(params.scale, pitch);

    const angle = (params.angle * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    // Squash across the flow direction so the noise stretches into strata.
    const along = (sample.x * cos + sample.y * sin) / (scale * params.strength);
    const across = (-sample.x * sin + sample.y * cos) / scale;

    const field = noise(along, across) * 0.5 + 0.5;
    // Triangular ramp per band keeps every contour a continuous ridge.
    const banded = field * params.bands;
    const local = banded - Math.floor(banded);
    return clamp01(1 - Math.abs(2 * local - 1));
  },
});

interface StarburstParams extends ParamValues {
  centerX: number;
  centerY: number;
  arms: number;
  twist: number;
  profile: WaveProfile;
  taper: number;
}

export const starburstPattern = definePattern<StarburstParams>({
  id: 'starburst',
  name: 'Starburst',
  description:
    'Radial arms sweeping out from a point on the wall, optionally twisted into a spiral. A feature motif meant to span the whole grid.',
  category: 'organic',
  tags: ['radial', 'feature', 'multi-panel'],
  params: [
    {
      kind: 'number',
      key: 'centerX',
      label: 'Centre X',
      min: -0.5,
      max: 1.5,
      step: 0.01,
      default: 0.5,
      group: 'Placement',
    },
    {
      kind: 'number',
      key: 'centerY',
      label: 'Centre Y',
      min: -0.5,
      max: 1.5,
      step: 0.01,
      default: 0.5,
      group: 'Placement',
    },
    { kind: 'int', key: 'arms', label: 'Arms', min: 2, max: 64, default: 12, group: 'Shape' },
    {
      kind: 'number',
      key: 'twist',
      label: 'Twist',
      min: -4,
      max: 4,
      step: 0.05,
      default: 0,
      help: 'Turns per wall radius; non-zero values spiral the arms.',
      group: 'Shape',
    },
    {
      kind: 'select',
      key: 'profile',
      label: 'Arm profile',
      options: PROFILE_OPTIONS,
      default: 'sine',
      group: 'Shape',
    },
    {
      kind: 'number',
      key: 'taper',
      label: 'Taper',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.4,
      help: 'Fades the arms out towards the wall edges.',
      group: 'Detail',
    },
  ],
  sample(sample, params) {
    const { wall } = sample;
    const cx = params.centerX * wall.width;
    const cy = params.centerY * wall.height;
    const dx = sample.x - cx;
    const dy = sample.y - cy;
    const radius = Math.hypot(dx, dy);
    const reach = Math.hypot(wall.width, wall.height) * 0.5;

    const angle = Math.atan2(dy, dx) / TAU;
    const phase =
      angle * params.arms + (radius / Math.max(reach, 1e-3)) * params.twist * params.arms;

    let height = waveProfile(phase, params.profile);
    if (params.taper > 0) {
      height *= lerp(1, 1 - smoothstep(0, reach, radius), params.taper);
    }
    return clamp01(height);
  },
});
